import React, { memo, useMemo } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import {
  CountryIconButton,
  MobileCountryBottomDrawer,
  MobileCountryDrawerHandle,
  MobileCountryDrawerContent,
  CountryOption
} from './styles';
import HeaderTooltip from './headerTooltip';
import { countries } from '../mocks/products.mock';
import { getSafeId } from '../../utils/helper';
import { TABS } from '../../constants';
import {Public as GlobeIcon} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { setCountryAndLocale, setActiveTab } from '@headless-shell/store';
import { HeaderNavigationCountryClick, HeaderNavigationCountrySelection, sendGA4Data } from '../../analytics/events';
import { countryNavigateUrl } from '../../utils/helper';

const CountryOptions = ({
  isMobile,
  selectedCountry,
  onCountrySelect
}) => {
  return countries.map((country) => (
    <CountryOption
      id={getSafeId(isMobile ? 'mobile-country' : 'country-option', country.name)}
      key={country.value}
      selected={selectedCountry === country.name}
      onClick={() => onCountrySelect(country.name, country.value)}
      isMobile={isMobile}
      tabIndex={0}
      role={isMobile ? "button" : "menuitem"}
      aria-label={`Select ${country.name}`}
    >
      {country.name}
    </CountryOption>
  ));
};

const CountrySelector = memo(({
  navigate
}) => {
  const selectedCountry = useSelector(state => state.app.country);
  const dispatch = useDispatch();
  const activeTab = useSelector(state => state.app.activeTab);
  const countryMenuOpen = activeTab === TABS.COUNTRY;
  const onCountryMenuToggle = () => {
    dispatch(setActiveTab(TABS.COUNTRY));

    sendGA4Data(HeaderNavigationCountryClick);
  }
  const onCountryMenuClose = () => dispatch(setActiveTab(''));
  const onCountrySelect = (country, countryValue) => {
    const countryUrl = countryNavigateUrl({ localeValue: countryValue });
    dispatch(setCountryAndLocale({ country, locale: countryValue }));
    dispatch(setActiveTab(''));
    navigate(countryUrl);

    sendGA4Data(HeaderNavigationCountrySelection, { eventLabel: country });
  };
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const mobileDrawerOpen = activeTab === TABS.MOBILECOUNTRY && !isDesktop;

  const onMobileDrawerClose = () => dispatch(setActiveTab(''));

  const desktopOptions = useMemo(() => (
    <Box role="menu" aria-label="Country menu">
      <CountryOptions
        isMobile={false}
        selectedCountry={selectedCountry}
        onCountrySelect={onCountrySelect}
      />
    </Box>
  ), [selectedCountry, onCountrySelect]);

  return (
    <React.Fragment>
      <HeaderTooltip
        variant="country"
        id="country-selector-tooltip"
        open={countryMenuOpen}
        title={desktopOptions}
        arrow
        placement="bottom-end"
        onClose={onCountryMenuClose}
      >
        <CountryIconButton
          id="country-selector-button"
          onClick={onCountryMenuToggle}
          aria-label={`Select country: ${selectedCountry}`}
          aria-expanded={countryMenuOpen}
          aria-haspopup="menu"
        >
          <GlobeIcon />
        </CountryIconButton>
      </HeaderTooltip>
      {mobileDrawerOpen !== undefined && (
        <MobileCountryBottomDrawer
          id="mobile-country-drawer"
          anchor="bottom"
          open={mobileDrawerOpen}
          onClose={onMobileDrawerClose}
          ModalProps={{ keepMounted: true }}
        >
          <MobileCountryDrawerContent 
          id="mobile-country-drawer-content"
          role="dialog"
          aria-label="Select country"
          aria-modal="true"
          >
            <MobileCountryDrawerHandle />
            <CountryOptions
              isMobile={true}
              selectedCountry={selectedCountry}
              onCountrySelect={onCountrySelect}
            />
          </MobileCountryDrawerContent>
        </MobileCountryBottomDrawer>
      )}
    </React.Fragment>
  );
});

CountrySelector.displayName = 'CountrySelector';


export default CountrySelector;